import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@flint/ui";
import { Field, SectionCard } from "./section-card.js";
import type { FormErrors, NewBillFormValues } from "./types.js";

interface BillIdentitySectionProps {
  values: NewBillFormValues;
  errors: FormErrors;
  onChange: <K extends keyof NewBillFormValues>(field: K, value: NewBillFormValues[K]) => void;
}

export function BillIdentitySection({ values, errors, onChange }: BillIdentitySectionProps) {
  return (
    <SectionCard
      step="2"
      title="Bill identity"
      description="The vendor's invoice number and dates — for matching to your GSTR-2B."
      action={
        <span className="rounded-full bg-sky-50 px-3 py-1 text-xs font-medium text-sky-700">
          Extracted from PDF
        </span>
      }
    >
      <div className="grid gap-3 md:grid-cols-4">
        <Field
          id="vendorDocNumber"
          label="Vendor invoice #"
          required
          error={errors.vendorDocNumber}
        >
          <Input
            id="vendorDocNumber"
            value={values.vendorDocNumber}
            onChange={(event) => onChange("vendorDocNumber", event.target.value)}
            placeholder="e.g. WI-2026-0412"
            className="font-mono"
          />
          {!errors.vendorDocNumber ? (
            <p className="text-xs font-medium text-ok">No duplicate found in last 12 months</p>
          ) : null}
        </Field>
        <Field id="receivedOn" label="Bill date" required error={errors.receivedOn}>
          <Input
            id="receivedOn"
            type="date"
            value={values.receivedOn}
            onChange={(event) => onChange("receivedOn", event.target.value)}
          />
          {!errors.receivedOn ? <p className="text-xs text-muted">As printed on the bill</p> : null}
        </Field>
        <Field id="dueOn" label="Due date" error={errors.dueOn}>
          <Input
            id="dueOn"
            type="date"
            value={values.dueOn}
            onChange={(event) => onChange("dueOn", event.target.value)}
          />
          {!errors.dueOn ? <p className="text-xs text-muted">Auto from net-15 terms</p> : null}
        </Field>
        <Field label="Payment terms">
          <Select
            value={values.paymentTerms}
            onValueChange={(value) => onChange("paymentTerms", value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Immediate">Immediate</SelectItem>
              <SelectItem value="15 days">15 days</SelectItem>
              <SelectItem value="30 days">30 days</SelectItem>
              <SelectItem value="Net 7">Net 7</SelectItem>
              <SelectItem value="Net 15">Net 15</SelectItem>
              <SelectItem value="Net 30">Net 30</SelectItem>
              <SelectItem value="Net 45">Net 45</SelectItem>
              <SelectItem value="Due on receipt">Due on receipt</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted">Default from vendor record</p>
        </Field>
      </div>
    </SectionCard>
  );
}
